import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { roles } from "util/config/roles";

export interface SettingsSideBarItem {
  key: string;
  name: string;
  parent: string;
  children?: SettingsSideBarItem[];
  forSuper: boolean;
}

interface SettingsSideBarProps {
  items: SettingsSideBarItem[];
  activeItemKey: string;
  onSelectItem: (key: string) => void;
}

const NavItem: React.FC<{
  item: SettingsSideBarItem;
  activeItemKey: string;
  onSelectItem: (key: string) => void;
  expandedKeys: Set<string>;
  onToggleExpand: (key: string) => void;
  level: number;
}> = ({ item, activeItemKey, onSelectItem, expandedKeys, onToggleExpand, level }) => {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedKeys.has(item.key);
  const { t } = useTranslation("pages");

  const handleClick = () => {
    if (hasChildren) {
      onToggleExpand(item.key);
    } else {
      onSelectItem(item.key);
    }
  };

  if (item.key.startsWith("divider")) {
    if (level > 0) return null;
    return <hr className="s_nav__divider" />;
  }

  return (
    <>
      <div
        className={`s_nav__item ${item.key === activeItemKey ? "selected" : ""} ${hasChildren ? "s_nav__item-parent" : ""}`}
        onClick={handleClick}
        style={{ paddingLeft: `${level * 15}px` }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleClick()}
      >
        <span>{t(`${item.name}`)} </span>
        {hasChildren && (<span className={`s_nav__item-chevron ${isExpanded ? "expanded" : ""}`}>
          &#9660;
        </span>)}
      </div>
      {hasChildren && isExpanded && (
        <div className="s_nav__item-children">
          {item.children!.map(child => (
            <NavItem
              key={child.key}
              item={child}
              activeItemKey={activeItemKey}
              onSelectItem={onSelectItem}
              expandedKeys={expandedKeys}
              onToggleExpand={onToggleExpand}
              level={level + 1}
            />
          ))}
          </div>
          )}
    </>
  );
};

const getInitialExpandedKeys = (items: SettingsSideBarItem[]): Set<string> => {
  const parentKeys = new Set<string>();
  items.forEach(item => {
    if (item.parent !== "super") {
      parentKeys.add(item.parent);
    }
  })
  return parentKeys;
};

const SettingsSideBar = ({
  items,
  activeItemKey,
  onSelectItem
}: SettingsSideBarProps) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => getInitialExpandedKeys(items));
  const role = roles();

  const onToggleExpand = (key: string) => {
    setExpandedKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const navTree = useMemo(() => {
    const filteredItems = items.filter(item => {
      if (role.isSuperAdmin && role.isAdmin) {
        return true;
      } else if (role.isSuperAdmin) {
        return true; // item.forSuper === true;
      } 

      return item.forSuper !== true;          
    });

    const tree: SettingsSideBarItem[] = [];
    const map: {[key: string]: SettingsSideBarItem} = {};

    filteredItems.forEach(item => {
      map[item.key] = { ...item, children: [] };
    });

    filteredItems.forEach(item => {
      if (item.parent && item.parent !== "super" && map[item.parent]) {
        map[item.parent].children?.push(map[item.key]);
      } else {
        tree.push(map[item.key]);
      }
    });

    return tree;
  }, [items, role]);

  return (
    <div className="s_settings_sidebar s_sidebar s_sidebar--show">
      <nav className="s_nav">
        {navTree.map((item) => (
            <NavItem
              key={item.key}
              item={item} 
              activeItemKey={activeItemKey}
              onSelectItem={onSelectItem}
              expandedKeys={expandedKeys}
              onToggleExpand={onToggleExpand}
              level={0}
            />
          )
        )}
      </nav>
    </div>
  );
};

export default SettingsSideBar;
